import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingBag } from 'lucide-react';
import useCartStore from '../store/cartStore';
import CartItem from '../components/CartItem';
import CheckoutForm from '../components/CheckoutForm';
import OrderSummary from '../components/OrderSummary';

const Cart = () => {
  const { items, updateQuantity, removeFromCart, getTotal, clearCart } = useCartStore();
  const [step, setStep] = useState('cart');
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [orderComplete, setOrderComplete] = useState(false);
  const [completedOrderId, setCompletedOrderId] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [searchParams] = useSearchParams();
  const [shippingMethod, setShippingMethod] = useState('postage');
  const [formErrors, setFormErrors] = useState({});
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [pickupLocation, setPickupLocation] = useState('Malvern East');
  const [sameAsBilling, setSameAsBilling] = useState(false);

  const [customerInfo, setCustomerInfo] = useState({
    name: '', email: '', phone: '',
    billingStreet: '', billingStreet2: '', billingSuburb: '', billingState: 'VIC', billingPostcode: '',
    shippingName: '', shippingEmail: '', shippingPhone: '',
    street: '', street2: '', suburb: '', state: 'VIC', postcode: ''
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkStripePayment(sessionId);
    }
  }, [searchParams]);

  const total = getTotal();
  const discount = promoApplied ? parseFloat((total * 0.10).toFixed(2)) : 0;
  const discountedTotal = total - discount;
  const shippingCost = shippingMethod === 'pickup' ? 0 : 11.95;
  const orderTotal = discountedTotal + shippingCost;

  const applyPromoCode = () => {
    if (promoCode.toUpperCase() === 'BEE10') {
      setPromoApplied(true);
      setPromoError('');
    } else {
      setPromoApplied(false);
      setPromoError('Invalid promo code');
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!customerInfo.name.trim()) errors.name = 'Name is required';
    if (!customerInfo.email.trim()) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(customerInfo.email)) errors.email = 'Invalid email';
    if (!customerInfo.phone.trim()) errors.phone = 'Phone is required';
    if (!customerInfo.billingStreet.trim()) errors.billingStreet = 'Billing address is required';
    if (!customerInfo.billingSuburb.trim()) errors.billingSuburb = 'Suburb is required';
    if (!customerInfo.billingPostcode.trim()) errors.billingPostcode = 'Postcode is required';
    if (shippingMethod === 'postage' && !sameAsBilling) {
      if (!customerInfo.street.trim()) errors.street = 'Street address is required';
      if (!customerInfo.suburb.trim()) errors.suburb = 'Suburb is required';
      if (!customerInfo.postcode.trim()) errors.postcode = 'Postcode is required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const inputClass = (field) =>
    `w-full px-4 py-3 rounded-lg border ${formErrors[field] ? 'border-red-400' : 'border-border'} bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm`;

  const saveOrder = async (paymentMethod, paymentId, overrideInfo) => {
    const info = overrideInfo || customerInfo;
    const currentShippingMethod = overrideInfo ? (overrideInfo._shippingMethod || shippingMethod) : shippingMethod;
    const currentSameAsBilling = overrideInfo ? (overrideInfo._sameAsBilling || sameAsBilling) : sameAsBilling;
    const currentPickupLocation = overrideInfo ? (overrideInfo._pickupLocation || pickupLocation) : pickupLocation;
    const currentPromoApplied = overrideInfo ? (overrideInfo._promoApplied || promoApplied) : promoApplied;
    const currentPromoCode = overrideInfo ? (overrideInfo._promoCode || promoCode) : promoCode;
    const currentDiscount = currentPromoApplied ? parseFloat((total * 0.10).toFixed(2)) : 0;
    const currentDiscountedTotal = total - currentDiscount;
    const currentShippingCost = currentShippingMethod === 'pickup' ? 0 : 11.95;
    const currentOrderTotal = currentDiscountedTotal + currentShippingCost;
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: items.map(item => ({
            name: item.name, size: item.selectedSize,
            quantity: item.quantity, price: item.itemPrice
          })),
          subtotal: parseFloat(total.toFixed(2)),
          discount: currentDiscount, promo_code: currentPromoApplied ? (currentPromoCode || 'BEE10') : null,
          shipping: currentShippingCost, total: parseFloat(currentOrderTotal.toFixed(2)),
          payment_method: paymentMethod, payment_id: paymentId || null,
          customer_name: info.name, customer_email: info.email,
          customer_phone: info.phone, shipping_method: currentShippingMethod,
          shipping_address: currentShippingMethod === 'postage' ? (currentSameAsBilling ? {
            name: info.name, email: info.email, phone: info.phone,
            street: info.billingStreet, street2: info.billingStreet2 || '',
            suburb: info.billingSuburb, state: info.billingState, postcode: info.billingPostcode
          } : {
            name: info.shippingName, email: info.shippingEmail, phone: info.shippingPhone,
            street: info.street, street2: info.street2 || '',
            suburb: info.suburb, state: info.state, postcode: info.postcode
          }) : currentShippingMethod === 'pickup' ? { pickup_location: currentPickupLocation } : null,
          billing_address: {
            street: info.billingStreet, street2: info.billingStreet2 || '',
            suburb: info.billingSuburb, state: info.billingState, postcode: info.billingPostcode
          }
        })
      });
      const data = await response.json();
      if (data.order_id) setCompletedOrderId(data.order_id);
    } catch (error) {
      console.error('Error saving order:', error);
    }
  };

  const checkStripePayment = async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      alert('Payment status check timed out. Please contact us if you completed the payment.');
      return;
    }
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stripe/checkout-status/${sessionId}`);
      if (!response.ok) throw new Error('Failed to check payment status');
      const data = await response.json();
      if (data.payment_status === 'paid') {
        const savedInfo = JSON.parse(sessionStorage.getItem('checkoutInfo') || '{}');
        const restoredCustomerInfo = savedInfo.customerInfo || {};
        restoredCustomerInfo._shippingMethod = savedInfo.shippingMethod;
        restoredCustomerInfo._pickupLocation = savedInfo.pickupLocation;
        restoredCustomerInfo._sameAsBilling = savedInfo.sameAsBilling;
        restoredCustomerInfo._promoApplied = savedInfo.promoApplied || false;
        restoredCustomerInfo._promoCode = savedInfo.promoCode || '';
        restoredCustomerInfo._discount = savedInfo.discount || 0;
        setCustomerInfo(restoredCustomerInfo);
        if (savedInfo.shippingMethod) setShippingMethod(savedInfo.shippingMethod);
        if (savedInfo.pickupLocation) setPickupLocation(savedInfo.pickupLocation);
        if (savedInfo.sameAsBilling !== undefined) setSameAsBilling(savedInfo.sameAsBilling);
        if (savedInfo.promoApplied) { setPromoApplied(true); setPromoCode(savedInfo.promoCode || 'BEE10'); }
        await saveOrder('stripe', sessionId, restoredCustomerInfo);
        setOrderComplete(true);
        clearCart();
        sessionStorage.removeItem('checkoutInfo');
        window.history.replaceState({}, document.title, '/cart');
      } else if (data.status === 'expired') {
        alert('Payment session expired. Please try again.');
      } else {
        setTimeout(() => checkStripePayment(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error('Error checking payment:', error);
    }
  };

  const handleStripeCheckout = async () => {
    setIsProcessing(true);
    sessionStorage.setItem('checkoutInfo', JSON.stringify({ customerInfo, shippingMethod, pickupLocation, sameAsBilling, promoCode, promoApplied, discount, name: customerInfo.name }));
    try {
      const currentUrl = window.location.origin;
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: parseFloat(orderTotal.toFixed(2)), currency: 'aud',
          items: items.map(item => ({ name: item.name, quantity: item.quantity, size: item.selectedSize, price: item.itemPrice })),
          success_url: `${currentUrl}/cart?session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${currentUrl}/cart`
        })
      });
      if (!response.ok) throw new Error('Failed to create checkout session');
      const data = await response.json();
      if (data.url) window.location.href = data.url;
    } catch (error) {
      console.error('Stripe checkout error:', error);
      alert('Failed to initiate checkout. Please try again.');
      setIsProcessing(false);
    }
  };

  const createOrder = (data, actions) => {
    return actions.order.create({
      purchase_units: [{ description: "Ricky's Bees Honey", amount: { value: orderTotal.toFixed(2) } }]
    });
  };

  const onApprove = (data, actions) => {
    return actions.order.capture().then(async () => {
      await saveOrder('paypal', data.orderID);
      setOrderComplete(true);
      clearCart();
    });
  };

  if (orderComplete) {
    return (
      <div className="min-h-screen pb-24 pt-48">
        <div className="max-w-4xl mx-auto px-4 md:px-8 lg:px-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center py-16">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 mb-6">
              <ShoppingBag className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Order Complete!
            </h1>
            {completedOrderId && (
              <p className="text-lg font-semibold text-primary mb-2">Order #{completedOrderId}</p>
            )}
            <p className="text-lg text-foreground/70 mb-8">
              Thank you for your order! Your honey will be freshly packed and shipped shortly.
            </p>
            <Link to="/products" className="inline-block px-8 py-4 bg-primary text-primary-foreground rounded-full hover:bg-primary/90 transition-all duration-300 font-semibold" data-testid="continue-shopping-button">
              Continue Shopping
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="min-h-screen pb-24 pt-48">
        <div className="max-w-4xl mx-auto px-4 md:px-8 lg:px-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center py-16" data-testid="empty-cart">
            <ShoppingBag className="w-16 h-16 text-muted-foreground mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Your cart is empty
            </h1>
            <p className="text-lg text-foreground/70 mb-8">Discover our range of pure, single-origin Australian honey.</p>
            <Link to="/products" className="inline-block px-8 py-4 bg-primary text-primary-foreground rounded-full hover:bg-primary/90 transition-all duration-300 font-semibold" data-testid="shop-now-empty-cart-button">
              Shop Now
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-24">
      <div className="max-w-6xl mx-auto px-4 md:px-8 lg:px-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-12" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="cart-headline">
            {step === 'cart' ? 'Your Cart' : 'Checkout'}
          </h1>

          {step === 'cart' && (
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
              <div className="lg:col-span-3 space-y-6">
                {items.map((item, index) => (
                  <CartItem key={`${item.id}-${item.selectedSize}`} item={item} index={index} updateQuantity={updateQuantity} removeFromCart={removeFromCart} />
                ))}
              </div>
              <OrderSummary items={items} total={total} setStep={setStep} isCheckout={false} />
            </div>
          )}

          {step === 'checkout' && (
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
              <CheckoutForm
                customerInfo={customerInfo} handleInputChange={handleInputChange}
                formErrors={formErrors} shippingMethod={shippingMethod}
                setShippingMethod={setShippingMethod} pickupLocation={pickupLocation}
                setPickupLocation={setPickupLocation} sameAsBilling={sameAsBilling}
                setSameAsBilling={setSameAsBilling} inputClass={inputClass}
              />
              <OrderSummary
                items={items} total={total} discount={discount} promoApplied={promoApplied}
                shippingMethod={shippingMethod} shippingCost={shippingCost} orderTotal={orderTotal}
                pickupLocation={pickupLocation}
                promoCode={promoCode} setPromoCode={setPromoCode} promoError={promoError}
                setPromoError={setPromoError} applyPromoCode={applyPromoCode} setPromoApplied={setPromoApplied}
                paymentMethod={paymentMethod} setPaymentMethod={setPaymentMethod}
                isProcessing={isProcessing} validateForm={validateForm}
                handleStripeCheckout={handleStripeCheckout} createOrder={createOrder}
                onApprove={onApprove} setStep={setStep} isCheckout={true}
              />
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Cart;
